import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../utilities/axios";
import dayjs from "dayjs";
import { updateProjectLockDate } from "./project";

const initialState = {
  // Compta Lock/Unlock states
  comptaSummary: null,
  comptaSummaryStatus: "idle",
  comptaHasNonReelTypes: false,
  comptaValidationStatus: "idle",
  comptaLockStatus: "idle",
  comptaUnlockStatus: "idle",

  // Treso Lock/Unlock states
  tresoSummary: null,
  tresoSummaryStatus: "idle",
  tresoHasNonPointedStatus: false,
  tresoValidationStatus: "idle",
  tresoLockStatus: "idle",
  tresoUnlockStatus: "idle",

  // Error states
  comptaError: null,
  tresoError: null,
};

// Compta Lock/Unlock Operations
export const getComptaSummary = createAsyncThunk(
  "projectLock/getComptaSummary",
  async (projectId) => {
    let data;
    try {
      const response = await axios.get(
        `api/projects/${projectId}/compta-summary`
      );
      data = await response.data;
      if (response.status === 200) {
        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(err.message ? err.message : data?.message);
    }
  }
);

export const checkComptaHasNonReelTypes = createAsyncThunk(
  "projectLock/checkComptaHasNonReelTypes",
  async ({ projectId, month, mode }) => {
    let data;
    try {
      const response = await axios.get(
        `api/projects/${projectId}/lock-compta/has-type-different-from-reel`,
        {
          params: {
            month: month,
          },
        }
      );
      data = await response.data;
      if (response.status === 200) {
        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(err.message ? err.message : data?.message);
    }
  }
);

export const lockComptaPeriod = createAsyncThunk(
  "projectLock/lockComptaPeriod",
  async ({ projectId, body }, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(
        `api/projects/${projectId}/lock/compta`,
        body
      );
      data = await response.data;
      if (response.status === 200) {
        // Update project lock date directly
        thunkAPI.dispatch(
          updateProjectLockDate({
            lockType: "compta",
            lockDate: body.lockMonth || null,
          })
        );

        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.message ? err.message : data?.message
      );
    }
  }
);

export const unlockComptaPeriod = createAsyncThunk(
  "projectLock/unlockComptaPeriod",
  async ({ projectId, body }, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(
        `api/projects/${projectId}/unlock/compta`,
        body
      );
      data = await response.data;
      if (response.status === 200) {
        const unlockMonth = body.unlockMonth;
        const newLockDate = dayjs(unlockMonth, "YYYY-MM")
          .subtract(1, "month")
          .format("YYYY-MM");

        thunkAPI.dispatch(
          updateProjectLockDate({
            lockType: "compta",
            lockDate: newLockDate,
          })
        );

        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.message ? err.message : data?.message
      );
    }
  }
);

// Treso Lock/Unlock Operations
export const getTresoSummary = createAsyncThunk(
  "projectLock/getTresoSummary",
  async (projectId) => {
    let data;
    try {
      const response = await axios.get(
        `api/projects/${projectId}/treso-summary`
      );
      data = await response.data;
      if (response.status === 200) {
        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(err.message ? err.message : data?.message);
    }
  }
);

export const checkTresoHasNonPointedStatus = createAsyncThunk(
  "projectLock/checkTresoHasNonPointedStatus",
  async ({ projectId, month, mode }) => {
    let data;
    try {
      const response = await axios.get(
        `api/projects/${projectId}/lock-treso/has-status-not-pointed`,
        {
          params: {
            month: month,
            mode: mode,
          },
        }
      );
      data = await response.data;
      if (response.status === 200) {
        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(err.message ? err.message : data?.message);
    }
  }
);

export const lockTresoPeriod = createAsyncThunk(
  "projectLock/lockTresoPeriod",
  async ({ projectId, body }, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(
        `api/projects/${projectId}/lock/treso`,
        body
      );
      data = await response.data;
      if (response.status === 200) {
        // Update project lock date directly
        thunkAPI.dispatch(
          updateProjectLockDate({
            lockType: "treso",
            lockDate: `${body.lockMonth}` || null,
          })
        );

        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.message ? err.message : data?.message
      );
    }
  }
);

export const unlockTresoPeriod = createAsyncThunk(
  "projectLock/unlockTresoPeriod",
  async ({ projectId, body }, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(
        `api/projects/${projectId}/unlock/treso`,
        { unlockMonth: body.unlockMonth }
      );
      data = await response.data;
      if (response.status === 200) {
        const unlockMonth = body.unlockMonth;
        const newLockDate = dayjs(unlockMonth, "YYYY-MM")
          .subtract(1, "month")
          .format("YYYY-MM");

        thunkAPI.dispatch(
          updateProjectLockDate({
            lockType: "treso",
            lockDate: newLockDate,
          })
        );
        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.message ? err.message : data?.message
      );
    }
  }
);

const slice = createSlice({
  name: "projectLock",
  initialState,
  reducers: {
    resetComptaStates: (state) => {
      state.comptaSummary = null;
      state.comptaSummaryStatus = "idle";
      state.comptaHasNonReelTypes = false;
      state.comptaValidationStatus = "idle";
      state.comptaLockStatus = "idle";
      state.comptaUnlockStatus = "idle";
      state.comptaError = null;
    },
    resetTresoStates: (state) => {
      state.tresoSummary = null;
      state.tresoSummaryStatus = "idle";
      state.tresoHasNonPointedStatus = false;
      state.tresoValidationStatus = "idle";
      state.tresoLockStatus = "idle";
      state.tresoUnlockStatus = "idle";
      state.tresoError = null;
    },
    resetAllStates: (state) => {
      return initialState;
    },
  },
  extraReducers: {
    // Compta Summary
    [getComptaSummary.pending]: (state) => {
      state.comptaSummaryStatus = "loading";
      state.comptaError = null;
    },
    [getComptaSummary.fulfilled]: (state, action) => {
      state.comptaSummaryStatus = "succeeded";
      state.comptaSummary = action.payload.data;
    },
    [getComptaSummary.rejected]: (state, action) => {
      state.comptaSummaryStatus = "failed";
      state.comptaError = action.error.message;
    },

    // Compta Validation Check
    [checkComptaHasNonReelTypes.pending]: (state) => {
      state.comptaValidationStatus = "loading";
      state.comptaError = null;
    },
    [checkComptaHasNonReelTypes.fulfilled]: (state, action) => {
      state.comptaValidationStatus = "succeeded";
      state.comptaHasNonReelTypes = action.payload.data;
    },
    [checkComptaHasNonReelTypes.rejected]: (state, action) => {
      state.comptaValidationStatus = "failed";
      state.comptaError = action.error.message;
    },

    // Compta Lock
    [lockComptaPeriod.pending]: (state) => {
      state.comptaLockStatus = "loading";
      state.comptaError = null;
    },
    [lockComptaPeriod.fulfilled]: (state) => {
      state.comptaLockStatus = "succeeded";
    },
    [lockComptaPeriod.rejected]: (state, action) => {
      state.comptaLockStatus = "failed";
      state.comptaError = action.payload;
    },

    // Compta Unlock
    [unlockComptaPeriod.pending]: (state) => {
      state.comptaUnlockStatus = "loading";
      state.comptaError = null;
    },
    [unlockComptaPeriod.fulfilled]: (state) => {
      state.comptaUnlockStatus = "succeeded";
    },
    [unlockComptaPeriod.rejected]: (state, action) => {
      state.comptaUnlockStatus = "failed";
      state.comptaError = action.payload;
    },

    // Treso Summary
    [getTresoSummary.pending]: (state) => {
      state.tresoSummaryStatus = "loading";
      state.tresoError = null;
    },
    [getTresoSummary.fulfilled]: (state, action) => {
      state.tresoSummaryStatus = "succeeded";
      state.tresoSummary = action.payload.data;
    },
    [getTresoSummary.rejected]: (state, action) => {
      state.tresoSummaryStatus = "failed";
      state.tresoError = action.error.message;
    },

    // Treso Validation Check
    [checkTresoHasNonPointedStatus.pending]: (state) => {
      state.tresoValidationStatus = "loading";
      state.tresoError = null;
    },
    [checkTresoHasNonPointedStatus.fulfilled]: (state, action) => {
      state.tresoValidationStatus = "succeeded";
      state.tresoHasNonPointedStatus = action.payload.data;
    },
    [checkTresoHasNonPointedStatus.rejected]: (state, action) => {
      state.tresoValidationStatus = "failed";
      state.tresoError = action.error.message;
    },

    // Treso Lock
    [lockTresoPeriod.pending]: (state) => {
      state.tresoLockStatus = "loading";
      state.tresoError = null;
    },
    [lockTresoPeriod.fulfilled]: (state) => {
      state.tresoLockStatus = "succeeded";
    },
    [lockTresoPeriod.rejected]: (state, action) => {
      state.tresoLockStatus = "failed";
      state.tresoError = action.payload;
    },

    // Treso Unlock
    [unlockTresoPeriod.pending]: (state) => {
      state.tresoUnlockStatus = "loading";
      state.tresoError = null;
    },
    [unlockTresoPeriod.fulfilled]: (state) => {
      state.tresoUnlockStatus = "succeeded";
    },
    [unlockTresoPeriod.rejected]: (state, action) => {
      state.tresoUnlockStatus = "failed";
      state.tresoError = action.payload;
    },
  },
});

export const { resetComptaStates, resetTresoStates, resetAllStates } =
  slice.actions;

export const reducer = slice.reducer;

export default slice;
